import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Paper } from "src/components";
import { Tab } from "src/components/tab";
import { theme } from "src/config";
import { Deliverable, Meeting, Question } from "src/models";
import { AuthStore, DeliverableStore, MeetingStore, MilestoneStore, QuestionStore, SpaceStore, UserStore } from "src/stores";
import styled, { css } from "styled-components";
import { StickyTableHeader } from "./components/stickyHeader";
import { TableRow } from "./components/tableRow";
import { DelivHeaderRow } from "./rows/delivHeaderRow";
import { DelivUserRow } from "./rows/delivUserRow";
import { MeetingRow } from "./rows/meetingRow";
import { MilestoneRow } from "./rows/milestoneRow";

const TABLE_HEADER = 5;

export interface IDeliverableBoardProps {
  authStore?:      AuthStore;
  delivStore?:     DeliverableStore;
  meetingStore?:   MeetingStore;
  milestoneStore?: MilestoneStore;
  spaceStore?:     SpaceStore;
  userStore?:      UserStore;
  questionStore?:  QuestionStore;
}

export const DelivBoard = inject("delivStore", "questionStore", "spaceStore", "milestoneStore", "userStore", "authStore", "meetingStore")(observer((props: IDeliverableBoardProps) => {
  const { delivStore, questionStore, userStore, authStore, spaceStore, milestoneStore, meetingStore } = props;
  const { t, i18n }     = useTranslation();
  const [view, setView] = React.useState<"days" | "weeks" | "months">("weeks");

  const users = userStore.getUsersBySpace(authStore.getCurRoomID, "all").filter( u => !u.getCurSpaceSettings.hide_in_matrix);
  const start = spaceStore.getItemByID(authStore.getCurProjectID)?.start_date || new Date();
  const end   = spaceStore.getItemByID(authStore.getCurProjectID)?.end_date || start;
  
  const questions: {[user_id: number]: Question[]} = React.useMemo(() => {
    const _q = questionStore.storage.board.getArray.reduce((res, val) => {
      if(!val.to) return res;
      res[val.to] = res[val.to] || [];
      res[val.to].push(val);
      return res;
    }, {});

    for (const u in _q)
      _q[u].sort(
        (q1: Question, q2: Question) =>
          q1.date.getTime() - q2.date.getTime()
      );

    return _q;
  }, [users, questionStore.storage.board.getArray]);

  const delivs: { [user_id: number]: Deliverable[] } = React.useMemo(() => {
    const _d = delivStore.storage.board.getArray.reduce((res, val) => {
      res[val.to] = res[val.to] || [];
      res[val.to].push(val);
      return res;
    }, {});
    for (const u in _d)
      _d[u].sort(
        (d1: Deliverable, d2: Deliverable) =>
          d1.date.getTime() - d2.date.getTime()
      );
    return _d;
  }, [users, delivStore.storage.board.getArray]);

  const meetings: Meeting[] = React.useMemo(() => {
    const _m = meetingStore.getItems.slice();
    _m.sort((m1: Meeting, m2: Meeting) => m1.date.getTime() - m2.date.getTime()); 
    return _m;
  }, [meetingStore.getItems]);

  return (
    <Paper
      $center
      $noShadow
      style       = {{ ...theme.paper.fullwidth, margin: theme.layout.gutter, overflow: "auto"}}
      $padding    = "0rem"
      $marginLeft = "2rem"
    >
      <StyledTable>
        <thead>
          <tr>
            <StickyTabHeader style={{padding: 0}}>
              <Box style={{height: '100%'}}>
                <StyledTab
                  onClick = {() => setView("days")}
                  active= { view === "days"}
                >
                  {t("day")}
                </StyledTab>
                <StyledTab
                  onClick = {() => setView("weeks")}
                  active= { view === "weeks"}
                >
                  {t("week")}
                </StyledTab> 
                <StyledTab
                  onClick = {() => setView("months")}
                  active= { view === "months"}
                >
                  {t("month")}
                </StyledTab>
              </Box>
            </StickyTabHeader>
            <DelivHeaderRow {...{view, start, end}}/>
          </tr>
          <StickyTopRow><MilestoneRow {...{view, start, end, milestones: milestoneStore.getItems}}/></StickyTopRow>
          <StickySecondRow><MeetingRow {...{view, start, end, meetings}}/></StickySecondRow>
        </thead>
        <tbody>
          {users.map(to => 
            <TableRow key={to.id}>
              <DelivUserRow 
                questions = {questions[to.id]}
                user      = {to}
                view      = {view}
                start     = {start}
                end       = {end}
                delivs    = {delivs[to.id]}
              />
            </TableRow>)
          }
        </tbody>
      </StyledTable>
    </Paper>
  );
}));  

const StyledTab = styled(Tab)`
  border-radius:    0;
  background-color: ${theme.color.slate2};

  &:first-child {
    border-radius: 0;
  }

  ${p => p.active && css`
    font-weight:     600;
    text-decoration: underline;
  `}
`;

const StyledTable = styled.table`
  min-width:       100%;
  border-collapse: separate;
  border-spacing:  0;
  table-layout:    fixed;
  
  th, td {
    vertical-align: middle;
    text-align:     center;
    border-bottom:  1px solid ${theme.color.primaryBorder};

    &:not(:last-of-type) {
      border-right: 1px solid ${theme.color.primaryBorder};
    }
  }

  thead {
    height:   ${TABLE_HEADER}rem;
    position: sticky;
    top:      0;
    z-index:  10;
  }

  td {
    min-width:    5rem;
    padding:      1.5rem;
    text-align:   center;
    align-items:  center;
    font-size:    ${theme.font.size.small};

    &:not(:first-of-type) {
      border-bottom: 1px solid ${theme.color.primaryBorder};
    }
  }

  th {
    padding:      0 0rem;
    height:       ${TABLE_HEADER}rem;
    min-width:    5rem;
    font-size:    ${theme.font.size.default};
    font-weight:  ${theme.font.weight.bold};
    cursor:       pointer;
    

    :hover {
      background-color: none;
    }
  }
`;

const StickyTabHeader = styled(StickyTableHeader)`
  padding:       0;
  width:         10rem;
  height:        0;
  border:        0;
  border-bottom: 1px solid ${theme.color.primaryBorder};
  border-right:  1px solid ${theme.color.primaryBorder};
  background-color: ${theme.color.slate2};
`

const StickyTopRow = styled(TableRow)`
  position:   sticky;
  top:        ${TABLE_HEADER}rem;
  background: white;
  z-index:    10;
`;

const StickySecondRow = styled(TableRow)`
  position:   sticky;
  top:        calc(${TABLE_HEADER}rem * 2);
  background: white;
  z-index:    10;
`;

const Box = styled.div<{ width?: string; justify?: string; align?: string }>`
  display:         flex;
  flex-direction:  row;
  width:           ${(props) => props.width};
  align-items:     ${(props) => props.align};
  justify-content: ${(props) => props.justify};
`;